import { ArrowBackIosNewRounded } from '@mui/icons-material';
import {
  Box,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  styled,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { SortDown, SortUp, TelephoneFill } from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';
import {
  Chat,
  ChatType,
  FilterType,
  ListingStatus,
  MessageType,
  PropertySubscription,
  UserType,
} from 'server-sdk/types';
import { BaseContainer, MarbleButton } from 'src/base';
import { ActiveChat, ActiveLeasingReport } from 'src/base/chat/ActiveChat';
import { ActiveChatTitle } from 'src/base/chat/ActiveChatTitle';
import { Chat as ChatBase } from 'src/base/chat/Chat';
import { ChatList } from 'src/base/chat/ChatList';
import { ChatListItem, NewChatListItem } from 'src/base/chat/ChatListItem';
import { MessageList } from 'src/base/chat/MessageList';
import { MessageListItemNew } from 'src/base/chat/MessageListItem';
import { MarbleIcon } from 'src/base/outputs/icons';
import { VoiceCallingModal } from 'src/base/outputs/VoiceCallingModal';
import { MarbleText } from 'src/base/texts/Text';
import { Experiment } from 'src/components/Experiment';
import { MarbleBlue } from 'src/Constants';
import { useVoice } from 'src/hooks/voice';
import { formatPhoneNumber } from 'src/utils/phone';
import {
  markChatSeen,
  useApiDispatch,
  useChat,
  useInboxChat,
  useLeasingInbox,
  useReduxState,
} from 'ui-sdk/src/hooks';
import { useRouter } from 'ui-sdk/src/router';
import { theme as marbleTheme } from 'ui-sdk/src/theme';

interface ChatComponentProps {
  activeChatId?: number;
  propId?: number;
  isLeasingMc?: boolean;
  textChat?: boolean;
  email?: boolean;
  demo?: boolean;
}

interface ActiveChatCompProps {
  activeChatId?: number;
  textChat?: boolean;
  email?: boolean;
}

interface ActiveLeasingReportCompProps {
  propId?: number;
  supportChatId?: number;
  demo?: boolean;
}

const ActiveChatComp = ({
  activeChatId,
  textChat,
  email,
}: ActiveChatCompProps) => {
  const user = useReduxState((state) => state.identity.user);
  const { chat, latestSentTextTime, send, sendText, sendEmail } = useChat(
    activeChatId,
    textChat,
    email,
  );

  const chatElementRef = useRef(null);
  useEffect(() => {
    chatElementRef.current?.scrollIntoView({ behavior: 'instant' });
  }, [chat?.id, latestSentTextTime]);

  const canUseCalling =
    chat?.leasingPropertyPhone &&
    !chat?.leasingPropertyPhone?.propertySubscribed;
  const currentFromPhone = chat?.leasingPropertyPhone?.phone;
  const currentToPhone = chat?.withPhones?.length ?
    chat?.withPhones[0]?.phone :
    undefined;
  const {
    currentCall,
    status,
    makeOutgoingCall,
    hangUpCall,
    acceptIncomingCall,
    rejectIncomingCall,
  } = useVoice(currentFromPhone, currentToPhone, canUseCalling);

  return (
    <ActiveChat
      chatId={activeChatId}
      onSubmit={(message) => {
        if (textChat) {
          sendText(message);
        } else if (email) {
          sendEmail(message);
        } else {
          send(message);
        }
      }}
      active={Boolean(activeChatId)}
      textChat={textChat}
      email={email}
    >
      <VoiceCallingModal
        currentCall={currentCall}
        toNumber={formatPhoneNumber(currentToPhone)}
        status={status}
        hangUp={hangUpCall}
        acceptIncomingCall={acceptIncomingCall}
        rejectIncomingCall={rejectIncomingCall}
      />
      <ActiveChatTitle>
        {chat?.name}
        {user?.id === 1 ?
          ` - ${chat?.withUsers[0].fullName} ${
              chat?.withUsers[0].type === UserType.Owner ?
                '(Owner)' :
                '(Renter)'
            }` :
          ''}
        {canUseCalling && (
          <IconButton
            onClick={async () => {
              await makeOutgoingCall();
            }}
            color="primary"
            size="large"
            style={{ padding: 5 }}
          >
            <TelephoneFill size={33} />
          </IconButton>
        )}
      </ActiveChatTitle>
      <MessageList>
        {chat?.messages
          ?.filter((m) => m.text !== '')
          .slice()
          .reverse()
          .map((msg, idx) => {
            let isRecent = false;
            if (idx > 0) {
              const next = chat.messages[idx - 1];
              if (next.creationTime - msg.creationTime < 600000) {
                isRecent = true;
              }
            }

            if (msg.message && msg.message.startsWith('media/')) {
              return (
                <MessageListItemNew
                  key={msg.id.toString()}
                  image={
                    msg?.content_type === MessageType.Image ||
                    msg?.content_type === MessageType.Pdf ?
                      `https://i.marblestatic.com/${msg.text}` :
                      undefined
                  }
                  video={
                    msg?.content_type === MessageType.Video ?
                      `https://i.marblestatic.com/${msg.text}` :
                      undefined
                  }
                  text={
                    msg?.content_type === MessageType.Text || undefined ?
                      msg.text :
                      ''
                  }
                  recent={isRecent}
                  sender={msg.isSentByMe}
                  date={new Date(msg.creationTime)}
                  callStatus={msg.callStatus}
                  callDuration={msg.callDuration}
                />
              );
            }
            return (
              <MessageListItemNew
                key={msg.id.toString()}
                image={msg.image}
                video={msg.video}
                text={msg.text}
                recent={isRecent}
                sender={msg.isSentByMe}
                date={new Date(msg.creationTime)}
                callStatus={msg.callStatus}
                callDuration={msg.callDuration}
              />
            );
          })}
        <div ref={chatElementRef} />
      </MessageList>
    </ActiveChat>
  );
};

const ActiveLeasingReportComp = ({
  propId,
  supportChatId,
  demo,
}: ActiveLeasingReportCompProps) => {
  const [leasingReport, setLeasingReport] = useState(undefined);
  const apiDispatch = useApiDispatch();
  useEffect(() => {
    apiDispatch(async (call, api) => {
      if (propId && !demo) {
        const report = await call(api.property.leasingReport, {
          property_id: propId,
        });
        if (!report) {
          return [false, 'failed to load leasing report'];
        }
        setLeasingReport(report);
      }
      return [true];
    });
  }, [propId]);

  return (
    <ActiveLeasingReport
      leasingReport={leasingReport}
      supportChatId={supportChatId}
      demo={demo}
    />
  );
};

export const ChatComponent: React.FC<ChatComponentProps> = (props) => {
  const dispatch = useDispatch();
  const [filterType, changeFilterType] = useState<FilterType>(FilterType.All);
  const [sortedDown, setSortedDown] = useState(true);
  const isLeasingMc = props.isLeasingMc ?? false;

  const { incrementPage, chats, startNewChat } = isLeasingMc ?
    useLeasingInbox() :
    useInboxChat();

  const router = useRouter();

  const user = useReduxState((state) => state.identity.user);

  const getUserTitle = (chat: Chat) => {
    if (
      chat.chatType === ChatType.OpsTask ||
      chat.chatType === ChatType.Maintenance
    ) {
      return chat.name;
    }
    if (chat.withUsers[0]) {
      const retVal =
        chat.withUsers[0].fullName +
        (user?.id === 1 ?
          ` (${
              chat.withUsers[0].type === UserType.Owner ? 'Owner' : 'Renter'
            })` :
          '');

      return retVal;
    }
  };
  const sortedChats = chats.sort((c1, c2) =>
    sortedDown ?
      c2.modificationTime - c1.modificationTime :
      c1.modificationTime - c2.modificationTime,
  );
  let chatList =
    sortedChats.length > 0 ?
      sortedChats.map((chat) => {
          if (!chat?.filterTypes?.includes(filterType)) {
            return null;
          }
          const active = props?.activeChatId === chat.id;

          const lastMessageText =
            !chat.messages[0]?.text ||
            chat.messages[0]?.text.startsWith('media/') ?
              chat.messages[0]?.image ||
                chat.messages[0]?.content_type === MessageType.Image ?
                'Image message' :
                'Video message' :
              chat.messages[0]?.text;
          const previewText =
            chat.messages.length > 0 ?
              `${chat.messages[0].user?.firstName}: ${
                  lastMessageText.length > 50 ?
                    `${lastMessageText.slice(0, 47)}...` :
                    lastMessageText
                }` :
              'No messages';
          const read =
            user?.id === 1 ?
              chat.messages[0].user?.id === 1 :
              chat.lastSeen > (chat.messages[0]?.creationTime ?? 0);

          return (
            <ChatListItem
              active={active}
              onClick={() => {
                dispatch(markChatSeen(chat.id, chat?.chatType));
                if (chat?.chatType === ChatType.TextMessage) {
                  router.push(`/owner/inbox/textchat/${chat.id}`);
                } else if (chat?.chatType === ChatType.Email) {
                  router.push(`/owner/inbox/email/${chat.id}`);
                } else {
                  router.push(
                    `${isLeasingMc ? '/leasingmc' : '/owner/inbox'}/${chat.id}`,
                  );
                }
              }}
              title={getUserTitle(chat)}
              secondaryTitle={chat?.secondaryName}
              preview={previewText}
              read={read}
              timeSince={chat?.timeSince}
            />
          );
        }) :
      [];

  chatList = chatList.filter((chat) => chat !== null);

  const properties = useReduxState((state) => state.portfolio.properties);
  const filteredProps = properties?.filter(
    (prop) => prop?.listing_status !== ListingStatus.Off,
  );
  const subscribed =
    properties?.find(
      (prop) => prop.subscription !== PropertySubscription.None,
    ) !== undefined;

  const leasingReports =
    filteredProps?.length > 0 && chats?.length > 0 ?
      filterType === FilterType.All || filterType === FilterType.Leasing ?
        filteredProps.map((prop) => {
            const active = props?.propId === prop.id;
            return (
              <ListItem
                key={`Weekly Leasing Report ${prop.address.addr1}`}
                onClick={() => {
                  router.push(`/owner/inbox/leasing-report/${prop.id}`);
                }}
                button
                style={{
                  backgroundColor: active ? '#e6e6e6' : 'white',
                  cursor: 'pointer',
                  paddingLeft: marbleTheme.spacing(6),
                  marginBlock: marbleTheme.spacing(2),
                  borderRadius: 5,
                  border: 1,
                }}
                selected={active}
              >
                <ListItemText
                  primary={
                    <MarbleText weight="med">Weekly Leasing Report</MarbleText>
                  }
                  secondary={
                    <div>
                      <MarbleText weight="med">{prop.address.addr1}</MarbleText>
                      <MarbleText style={{ opacity: '50%' }}>
                        View your weekly leasing summary.
                      </MarbleText>
                    </div>
                  }
                />
              </ListItem>
            );
          }) :
        [] :
      (filterType === FilterType.All || filterType === FilterType.Leasing) &&
        !subscribed ?
      [
          <NewChatListItem
            active={false}
            onClick={() => {
              router.push(`/owner/inbox/leasing-report/demo`);
            }}
            title="Weekly Leasing Report (demo)"
            subtitle="1234 Marble St."
            preview="View your weekly leasing summary."
            read
          />,
        ] :
      [];

  const children =
    chatList?.length > 0 || leasingReports?.length > 0 ?
      [...chatList, ...leasingReports] :
      [
          <MarbleText style={{ opacity: '50%' }}>
            {`There are no ${FilterType[
              filterType
            ].toLowerCase()} chats at the moment`}
          </MarbleText>,
        ];

  const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
      marginRight: theme.spacing(1),
      border: 0,
      '&.Mui-selected': {
        borderRadius: theme.spacing(1),
        color: marbleTheme.colors.blue.main,
        backgroundColor: marbleTheme.colors.blue.glass,
      },
      '&.Mui-selected p': {
        color: marbleTheme.colors.blue.main,
      },
      '&:hover': {
        borderRadius: theme.spacing(1),
      },
    },
  }));

  return (
    <BaseContainer wide padding={5}>
      {(props.activeChatId || props.propId || props.demo) && (
        <Box
          paddingLeft={marbleTheme.spacing(3)}
          display={{ xs: 'block', md: 'none' }}
        >
          <MarbleButton
            title="Inbox"
            color="gray"
            icon={<ArrowBackIosNewRounded />}
            variant="contained"
            onClick={() => {
              router.push('/owner/inbox');
            }}
          />
        </Box>
      )}
      <Box
        display={
          props.activeChatId || props.propId || props.demo ?
            { xs: 'none', md: 'block' } :
            null
        }
      >
        <MarbleText
          size="xl"
          weight="bold"
          style={{
            paddingLeft: marbleTheme.spacing(4),
            paddingBottom: marbleTheme.spacing(4),
          }}
        >
          All Messages
        </MarbleText>
        <Experiment
          control={<div />}
          b={
            <MarbleButton
              icon={<MarbleIcon name="plus" />}
              title="New Chat"
              onClick={() => {
                startNewChat();
              }}
            />
          }
        />
        <StyledToggleButtonGroup
          value={filterType}
          exclusive
          onChange={(event, value) => {
            if (value !== null) {
              if (value === -1) {
                setSortedDown(!sortedDown);
                return;
              }
              changeFilterType(value);
            }
          }}
          sx={{
            paddingLeft: marbleTheme.spacing(4),
          }}
        >
          <ToggleButton value={FilterType.All} sx={{ fontSize: '18px' }}>
            <MarbleText>All</MarbleText>
          </ToggleButton>
          <ToggleButton value={FilterType.Unread} sx={{ fontSize: '18px' }}>
            <MarbleText>Unread</MarbleText>
          </ToggleButton>
          <ToggleButton value={FilterType.Leasing} sx={{ fontSize: '18px' }}>
            <MarbleText>Leasing</MarbleText>
          </ToggleButton>
          <ToggleButton
            value={FilterType.Maintenance}
            sx={{ fontSize: '18px' }}
          >
            <MarbleText>Work Orders</MarbleText>
          </ToggleButton>
          <ToggleButton value={-1} sx={{ fontSize: '18px' }}>
            {sortedDown ? (
              <SortDown color={MarbleBlue} />
            ) : (
              <SortUp color={MarbleBlue} />
            )}
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Box>
      <ChatBase>
        <Grid container>
          <Grid item xs={0} md={3} pl={0} sx={{ height: '100%' }}>
            <Box
              sx={{ height: '100%' }}
              display={
                props.activeChatId || props.propId || props.demo ?
                  { xs: 'none', md: 'block' } :
                  null
              }
            >
              <ChatList handleBottom={incrementPage}>{children}</ChatList>
            </Box>
          </Grid>
          <Grid item xs={12} md={9} sx={{ height: '100%', padding: 0 }}>
            <ActiveChatComp
              activeChatId={props.activeChatId}
              textChat={props.textChat}
              email={props.email}
            />
            {(props.propId || props.demo) && (
              <ActiveLeasingReportComp
                demo={props?.demo}
                propId={props.propId}
                supportChatId={
                  chats.find((chat) => chat.chatType === ChatType.MC)?.id
                }
              />
            )}
          </Grid>
        </Grid>
      </ChatBase>
    </BaseContainer>
  );
};
